import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningIcon from "@mui/icons-material/Warning";
import BoltIcon from "@mui/icons-material/Bolt";
import StorageIcon from "@mui/icons-material/Storage";
import DatasetOutlinedIcon from "@mui/icons-material/DatasetOutlined";
import HeaderNew from "common/components/custom/header-new/HeaderNew";
import HubHeader from "common/components/custom/hub-header/HubHeader";
import ConfirmationModal from "common/components/custom/confirmation-modal/ConfirmationModal";
import ConnectionConfigModal from "../components/connection-config-modal/ConnectionConfigModal";
import { MainRoutes } from "common/config/route.config";
import {
  DataSource,
  dataSources,
  Connection,
  DataFlow,
  DataSchema,
  DataFilter,
  DataMapping,
  WorkflowStep
} from "../../data/models";
import { FlowStorage } from "../../data/flow-storage";
import {
  SnowflakeService,
  SnowflakeConfigRequest
} from "../../data/services/snowflake.service";
import "./DataIntegration.scss";

const DataIntegration: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("connections");
  const [workflowStep, setWorkflowStep] = useState<WorkflowStep>("hub");

  // Check URL parameter to set active tab
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl === "flows") {
      setActiveTab("flows");
    }
  }, [searchParams]);

  const [connections, setConnections] = useState<Connection[]>([
    {
      id: "1",
      name: "Production Snowflake",
      dataSourceType: "snowflake",
      status: "active",
      config: {},
      createdAt: "2024-11-15",
      lastUsed: "2024-11-17"
    }
  ]);
  const [flows, setFlows] = useState<DataFlow[]>([]);
  const [selectedDataSource, setSelectedDataSource] =
    useState<DataSource | null>(null);
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [editingConnection, setEditingConnection] = useState<Connection | null>(
    null
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    id: string;
    name: string;
    type: "connection" | "flow";
  } | null>(null);

  // Simplified workflow state
  const [selectedConnection, setSelectedConnection] =
    useState<Connection | null>(null);

  // Load flows from storage on component mount and when tab changes
  useEffect(() => {
    if (activeTab === "flows") {
      const savedFlows = FlowStorage.getFlows();
      setFlows(savedFlows);
    }
  }, [activeTab]);

  // Format date helper
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    } catch {
      return dateString;
    }
  };

  const handleAddConnection = (dataSource: DataSource) => {
    setSelectedDataSource(dataSource);
    setEditingConnection(null);
    setIsEditMode(false);
    setShowConnectionModal(true);
  };

  const handleEditConnection = (connection: Connection) => {
    const dataSource = getDataSourceInfo(connection.dataSourceType);
    if (dataSource) {
      setSelectedDataSource(dataSource);
      setEditingConnection(connection);
      setIsEditMode(true);
      setShowConnectionModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowConnectionModal(false);
    setSelectedDataSource(null);
    setEditingConnection(null);
    setIsEditMode(false);
  };

  const handleSaveConnection = async (config: {
    name: string;
    config: Record<string, string>;
  }) => {
    if (!selectedDataSource) return;

    // Call Snowflake config API for snowflake data source
    if (selectedDataSource.type === "snowflake") {
      const payload: SnowflakeConfigRequest = {
        user: config.config.username,
        password: config.config.password,
        account: config.config.account,
        warehouse: config.config.warehouse,
        database: config.config.database,
        db_schema: config.config.schema || "PUBLIC"
      };

      try {
        const response = await SnowflakeService.saveConfig(payload);

        if (response.error) {
          console.error("Failed to save Snowflake config:", response.error);
          return;
        }
      } catch (error) {
        console.error("Error saving Snowflake config:", error);
        return;
      }
    }

    if (isEditMode && editingConnection) {
      // Update existing connection
      const updatedConnection: Connection = {
        ...editingConnection,
        name: config.name,
        config: config.config
      };

      setConnections(
        connections.map((conn) =>
          conn.id === editingConnection.id ? updatedConnection : conn
        )
      );
    } else {
      // Create new connection
      const newConnection: Connection = {
        id: Date.now().toString(),
        name: config.name,
        dataSourceType: selectedDataSource.type,
        status: "active",
        config: config.config,
        createdAt: new Date().toISOString().split("T")[0]
      };

      setConnections([...connections, newConnection]);

      // For Snowflake, navigate to Snowflake Data Viewer after saving
      if (selectedDataSource.type === "snowflake") {
        handleCloseModal();
        navigate(
          `${MainRoutes.SNOWFLAKE_DATA_VIEWER}?connection=${encodeURIComponent(
            config.name
          )}`
        );
        return;
      }
    }

    handleCloseModal();
  };

  const handleDeleteConnection = (id: string) => {
    const connectionToDelete = connections.find((c) => c.id === id);
    if (connectionToDelete) {
      setItemToDelete({
        id: connectionToDelete.id,
        name: connectionToDelete.name,
        type: "connection"
      });
      setShowDeleteModal(true);
    }
  };

  const handleDeleteFlow = (id: string) => {
    const flowToDelete = flows.find((f) => f.id === id);
    if (flowToDelete) {
      setItemToDelete({
        id: flowToDelete.id,
        name: flowToDelete.name,
        type: "flow"
      });
      setShowDeleteModal(true);
    }
  };

  const handleDeleteConfirm = () => {
    if (itemToDelete) {
      if (itemToDelete.type === "connection") {
        setConnections(connections.filter((c) => c.id !== itemToDelete.id));
      } else if (itemToDelete.type === "flow") {
        // Delete from storage and update state
        FlowStorage.deleteFlow(itemToDelete.id);
        setFlows(flows.filter((f) => f.id !== itemToDelete.id));
      }
    }
    handleDeleteCancel();
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const getStatusIcon = (status: Connection["status"]) => {
    switch (status) {
      case "active":
        return <CheckCircleIcon className="status-icon active" />;
      case "error":
        return <CancelIcon className="status-icon error" />;
      default:
        return <WarningIcon className="status-icon inactive" />;
    }
  };

  const getDataSourceInfo = (type: string) => {
    return dataSources.find((ds) => ds.type === type);
  };

  const handleCreateFlow = () => {
    navigate(MainRoutes.FLOW_BUILDER);
  };

  const handleEditFlow = (flowId: string) => {
    navigate(`${MainRoutes.FLOW_BUILDER}?edit=${flowId}`);
  };

  // Oil & Gas Workflow handlers
  const handleStartAugmentation = (connection: Connection) => {
    // Navigate to AvailableDataTable route with connection info
    navigate(
      `/data-integration/available-details?connection=${encodeURIComponent(
        connection.name
      )}`
    );
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <>
      <HeaderNew showSearch={false} />
      <div className="data-integration-hub">
        <HubHeader
          title="Data Integration Hub"
          description="Centralized platform for seamless data integration and workflow management."
        />

        <div className="hub-content">
          <div className="tab-navigation">
            <button
              className={`tab ${activeTab === "connections" ? "active" : ""}`}
              onClick={() => setActiveTab("connections")}
            >
              Connections
            </button>
            <button
              className={`tab ${activeTab === "flows" ? "active" : ""}`}
              onClick={() => setActiveTab("flows")}
            >
              Data Flows
            </button>
            <button
              className={`tab ${activeTab === "sources" ? "active" : ""}`}
              onClick={() => setActiveTab("sources")}
            >
              Available Sources
            </button>
          </div>

          {activeTab === "connections" && (
            <div className="tab-content">
              {/* Connections */}
              <div className="section-header">
                <div className="section-info">
                  <h2>Your Connections</h2>
                  <p>Manage your data source connections</p>
                </div>
                <button
                  className="primary-button"
                  onClick={() => setActiveTab("sources")}
                >
                  <AddIcon /> Add Connection
                </button>
              </div>

              <div className="connections-grid">
                {connections.map((connection) => {
                  const sourceInfo = getDataSourceInfo(
                    connection.dataSourceType
                  );
                  return (
                    <div key={connection.id} className="connection-card">
                      <div className="card-header">
                        <div className="connection-info">
                          <span className="connection-icon">
                            {sourceInfo?.icon}
                          </span>
                          <div className="connection-details">
                            <h3>{connection.name}</h3>
                            <p>{sourceInfo?.name}</p>
                          </div>
                        </div>
                        {getStatusIcon(connection.status)}
                      </div>

                      <div className="connection-meta">
                        <div className="left-column">
                          <div className="info-item">
                            <span className="info-label">Created:</span>
                            <span className="info-value">
                              {connection.createdAt}
                            </span>
                          </div>
                        </div>
                        <div className="right-column">
                          {connection.lastUsed && (
                            <div className="info-item">
                              <span className="info-label">Last used:</span>
                              <span className="info-value">
                                {connection.lastUsed}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="connection-actions">
                        {connection.dataSourceType === "snowflake" && (
                          <button
                            className="primary-button full-width"
                            onClick={() => handleStartAugmentation(connection)}
                          >
                            <BoltIcon /> Create Augmentation Flow
                          </button>
                        )}
                        <div className="action-buttons">
                          <button
                            className="secondary-button edit-btn"
                            onClick={() => handleEditConnection(connection)}
                          >
                            <EditIcon /> Edit
                          </button>
                          <button
                            className="secondary-button delete-btn"
                            onClick={() =>
                              handleDeleteConnection(connection.id)
                            }
                          >
                            <DeleteIcon /> Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === "flows" && (
            <div className="tab-content">
              <div className="section-header">
                <div className="section-info">
                  <h2>Data Flows</h2>
                  <p>Create and manage data integration flows</p>
                </div>
                <button className="primary-button" onClick={handleCreateFlow}>
                  <AddIcon /> Create Flow
                </button>
              </div>

              {flows.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-content">
                    <h3>No flows created yet</h3>
                    <p>
                      Create your first data flow to start moving data between
                      your sources and destinations
                    </p>
                    <button
                      className="primary-button"
                      onClick={handleCreateFlow}
                    >
                      <AddIcon /> Create Your First Flow
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flows-grid">
                  {flows.map((flow) => (
                    <div key={flow.id} className="flow-card">
                      <div className="card-header">
                        <div className="card-title">
                          {/* <StorageIcon className="card-icon" /> */}
                          <DatasetOutlinedIcon className="card-icon" />
                          <h3>{flow.name}</h3>
                        </div>
                      </div>

                      <div className="card-content">
                        <div className="meta-grid">
                          {/* <div className="meta-item">
                            <span className="meta-label">DESCRIPTION :</span>
                            <span className="meta-value">{flow.description || 'No description'}</span>
                          </div> */}
                          <div className="meta-item">
                            <span className="meta-label">STATUS :</span>
                            <span className="meta-value">{flow.status}</span>
                          </div>
                          <div className="meta-item">
                            <span className="meta-label">CREATED ON :</span>
                            <span className="meta-value">
                              {formatDate(flow.createdAt)}
                            </span>
                          </div>
                          {/* <div className="meta-item">
                            <span className="meta-label">LAST RUN :</span>
                            <span className="meta-value">{flow.lastRun ? formatDate(flow.lastRun) : 'Never'}</span>
                          </div> */}
                        </div>
                      </div>

                      <div className="card-actions">
                        {flow.status === "active" ? (
                          <button className="action-button">
                            <PauseIcon />
                          </button>
                        ) : (
                          <button className="action-button">
                            <PlayArrowIcon />
                          </button>
                        )}
                        <button
                          className="action-button"
                          onClick={() => handleEditFlow(flow.id)}
                        >
                          <EditIcon />
                        </button>
                        <button
                          className="action-button delete-btn"
                          onClick={() => handleDeleteFlow(flow.id)}
                        >
                          <DeleteIcon />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Available Sources */}
          {activeTab === "sources" && (
            <div className="tab-content">
              <div className="section-header">
                <div className="section-info">
                  <h2>Available Data Sources</h2>
                  <p>Select a data source to create a new connection</p>
                </div>
              </div>

              <div className="sources-grid">
                {dataSources.map((source) => (
                  <div
                    key={source.id}
                    className="source-card"
                    onClick={() => handleAddConnection(source)}
                  >
                    <div className="source-header">
                      <div className="source-info">
                        <span className="source-icon">{source.icon}</span>
                        <div className="source-details">
                          <h3>{source.name}</h3>
                          <span className="category-badge">
                            {source.category}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p>{source.description}</p>
                    <button className="secondary-button full-width">
                      <AddIcon /> Add Connection
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <ConnectionConfigModal
        isOpen={showConnectionModal}
        onClose={handleCloseModal}
        onSave={handleSaveConnection}
        dataSource={selectedDataSource}
        isEditMode={isEditMode}
        existingConnection={editingConnection}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        title={`Delete ${
          itemToDelete?.type === "connection" ? "Connection" : "Flow"
        }`}
        message={`Are you sure you want to delete "${itemToDelete?.name}"? This action cannot be undone.`}
        variant="danger"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
};

export default DataIntegration;
